import { forwardRef } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AnalysesPDF from '../../pdf/AnalysesPDF';
import type { Analyse } from '../../types/analyse';
import AnalysesContent from './AnalysesContent';

interface AnalysesViewProps {
  analyses: Analyse[];
  patientInfo: {
    nom: string;
    prenom: string;
    dateNaissance: string;
    adresse?: string;
  };
  doctorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    specialty?: string; // ✅ Ajouter la spécialité du médecin
  };
  consultationDate: string;
  instructions?: string; 
  onBack: () => void;
  onPrint: () => void;
  onDownloadPDF: () => void;
  isDownloading?: boolean;
}

const AnalysesView = forwardRef<HTMLDivElement, AnalysesViewProps>(({
  analyses,
  patientInfo,
  doctorInfo,
  consultationDate,
  instructions, 
  onBack,
  onPrint,
  onDownloadPDF,
  isDownloading
}, ref) => {
  // Debug: Vérifier les props reçues
  console.log("📋 AnalysesView props:", { analyses, patientInfo, doctorInfo, consultationDate, instructions, onBack, onPrint, onDownloadPDF, isDownloading });

  return (
    <div ref={ref} className="min-h-screen bg-white">
      <div className="bg-gray-100 p-4 flex justify-between items-center print:hidden">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
          aria-label="Retour à la page précédente"
        >
          Retour
        </button>

        <div className="flex gap-3">
          <button
            onClick={onPrint}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
            aria-label="Imprimer la demande d'analyses"
          >
            <span className="hidden sm:inline">Imprimer</span>
          </button>
          <PDFDownloadLink
            document={
              <AnalysesPDF
                analyses={analyses}
                patientInfo={patientInfo}
                doctorInfo={doctorInfo}
                consultationDate={consultationDate}
                instructions={instructions}
              />
            }
            fileName={`demande_analyses_${patientInfo.nom.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
          >
            {({ loading }) => (
              <>
                {loading ? (
                  <span className="inline-flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="ml-2">Génération...</span>
                  </span>
                ) : (
                  <span className="flex items-center">
                    <span className="hidden sm:inline">Télécharger PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </span>
                )}
              </>
            )}
          </PDFDownloadLink>
        </div>
      </div>

      <div className="analyses-content">
        <AnalysesContent
          analyses={analyses}
          patientInfo={patientInfo}
          doctorInfo={doctorInfo}
          consultationDate={consultationDate}
          instructions={instructions}
        />
      </div>

      <div className="print:hidden flex justify-end px-4 mt-2">
        <PDFDownloadLink
          document={
            <AnalysesPDF
              analyses={analyses}
              patientInfo={patientInfo}
              doctorInfo={doctorInfo}
              consultationDate={consultationDate}
              instructions={instructions}
            />
          }
          fileName={`demande_analyses_${patientInfo.nom.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`}
          className="flex items-center gap-2 px-3 py-2 bg-white text-green-600 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors"
        >
          Télécharger PDF (A5)
        </PDFDownloadLink>
      </div>
    </div>
  );
});

export default AnalysesView;